const CARD_RATIOS = ['portrait', 'portrait', 'tall', 'portrait', 'portrait', 'square', 'portrait', 'portrait']

export function isInspirationApiSuccess(res) {
  return !!(res && (res.code === 0 || res.code === '0'))
}

export function normalizeCoverUrls(coverUrls) {
  if (!Array.isArray(coverUrls)) return []
  return coverUrls.map((url) => String(url || '').trim()).filter(Boolean)
}

/** categoryTypeText 为「视频」或 categoryType=2 */
export function isVideoInspirationItem(item) {
  if (String(item?.categoryTypeText || '').trim() === '视频') return true
  return Number(item?.categoryType) === 2
}

/** 列表封面：coverUrls[0] */
export function getInspirationCoverUrl(coverUrls) {
  const urls = normalizeCoverUrls(coverUrls)
  return urls[0] || ''
}

/** 视频预览/播放：coverUrls 序号 1（下标 1） */
export function getInspirationVideoUrl(coverUrls) {
  const urls = normalizeCoverUrls(coverUrls)
  return urls[1] || ''
}

export function mapCategoryTypeToCardType(item) {
  return isVideoInspirationItem(item) ? 'video' : 'image'
}

export function pickCardRatio(seed) {
  const n = Math.abs(Number(seed) || 0)
  return CARD_RATIOS[n % CARD_RATIOS.length]
}

/**
 * 列表/发现区卡片：映射 API 字段到页面已有 card 结构
 */
export function mapInspirationItemToCard(item, tabId = '') {
  if (!item || item.id == null) return null
  const coverUrls = normalizeCoverUrls(item.coverUrls)
  const cover = getInspirationCoverUrl(coverUrls)
  const isVideo = isVideoInspirationItem(item)
  const type = isVideo ? 'video' : 'image'
  const previewVideoUrl = isVideo ? getInspirationVideoUrl(coverUrls) : ''
  const playUrl = isVideo ? getInspirationVideoUrl(coverUrls) : ''
  return {
    id: String(item.id),
    title: String(item.title || '').trim(),
    type,
    categoryTypeText: String(item.categoryTypeText || '').trim(),
    cover,
    coverUrls,
    previewVideoUrl,
    playUrl,
    ratio: pickCardRatio(item.id),
    tabId,
    description: String(item.description || '').trim(),
    categoryNames: Array.isArray(item.categoryNames)
      ? item.categoryNames.map((n) => String(n || '').trim()).filter(Boolean)
      : [],
    modelName: String(item.modelName || '').trim(),
  }
}

/** paramInfo.type=image：图片类灵感案例 */
export function isInspirationImageCase(paramInfo, cardType = '') {
  const info = typeof paramInfo === 'object' && paramInfo !== null && !Array.isArray(paramInfo)
    ? paramInfo
    : parseInspirationParamInfo(paramInfo)
  const type = String(info.type || '').trim().toLowerCase()
  if (type === 'image') return true
  if (String(cardType || '').trim().toLowerCase() !== 'image') return false
  return !!(
    info.prompt
    || info.productImages
    || info.detailImageOptions
    || info.setImageCategories
    || info.imageType
  )
}

const IMAGE_TYPE_LABELS = {
  set: '商品套图',
  detail: '商品详情图',
}

export function getInspirationImageTypeLabel(imageType) {
  const key = String(imageType || '').trim().toLowerCase()
  return IMAGE_TYPE_LABELS[key] || key || '商品套图'
}

function extractInspirationImageUrl(item) {
  if (!item) return ''
  if (typeof item === 'string') return item.trim()
  return String(
    item.url || item.src || item.ossUrl || item.coverUrl || item.mediaUrl || '',
  ).trim()
}

/** 成果图：paramInfo.images */
export function parseInspirationResultImages(detail, paramInfo) {
  const info = typeof paramInfo === 'object' && paramInfo !== null && !Array.isArray(paramInfo)
    ? paramInfo
    : parseInspirationParamInfo(paramInfo)
  const list = info.images
  if (Array.isArray(list) && list.length) {
    return list
      .map((item, idx) => {
        const url = extractInspirationImageUrl(item)
        if (!url) return null
        const name = typeof item === 'object' && item
          ? String(item.name || item.label || item.title || `成果图 ${idx + 1}`).trim()
          : `成果图 ${idx + 1}`
        return {
          id: String(item?.id || `result-${idx}`),
          url,
          name,
        }
      })
      .filter(Boolean)
  }
  return normalizeCoverUrls(detail?.coverUrls).map((url, idx) => ({
    id: `result-${idx}`,
    url,
    name: `成果图 ${idx + 1}`,
  }))
}

/** 参考图：paramInfo.productImages */
export function parseInspirationProductImages(paramInfo) {
  const info = typeof paramInfo === 'object' && paramInfo !== null && !Array.isArray(paramInfo)
    ? paramInfo
    : parseInspirationParamInfo(paramInfo)
  const list = info.productImages
  if (!Array.isArray(list)) return []
  return list
    .map((item, idx) => {
      const url = extractInspirationImageUrl(item)
      if (!url) return null
      return {
        id: String(item?.id || `ref-${idx}`),
        type: 'image',
        url,
        name: String(item?.name || item?.label || `参考图 ${idx + 1}`).trim(),
        objectKey: String(item?.objectKey || '').trim(),
      }
    })
    .filter(Boolean)
}

/** 出图类型配置：paramInfo.detailImageOptions / setImageCategories */
export function parseInspirationDetailImageOptions(paramInfo) {
  const info = typeof paramInfo === 'object' && paramInfo !== null && !Array.isArray(paramInfo)
    ? paramInfo
    : parseInspirationParamInfo(paramInfo)
  const list = info.detailImageOptions || info.setImageCategories
  if (!Array.isArray(list)) return []
  return list
    .map((item, idx) => {
      if (!item || typeof item !== 'object') return null
      return {
        id: String(item.id || `opt-${idx}`),
        label: String(item.label || item.name || '').trim(),
        desc: String(item.desc || item.description || '').trim(),
        checked: item.checked != null ? !!item.checked : Number(item.count) > 0,
        count: Number(item.count) || 0,
      }
    })
    .filter(Boolean)
}

function formatInspirationModelLabel(modelId, fallback = '') {
  const id = String(modelId || '').trim()
  if (!id) return fallback
  const map = {
    'gpt-image-2': 'GPT Image 2',
    'seedream-4': 'Seedream 4',
    'seedream-4.5': 'Seedream 4.5',
  }
  return map[id] || fallback || id
}

function parseAspectRatioValue(ratioText) {
  const text = String(ratioText || '').trim()
  if (!text || !text.includes(':')) return null
  const [w, h] = text.split(':').map((n) => Number(n))
  if (!w || !h) return null
  return w / h
}

/** 图片类详情右侧参数摘要 */
export function buildInspirationImageParamSummary(paramInfo, detail = {}) {
  const info = typeof paramInfo === 'object' && paramInfo !== null && !Array.isArray(paramInfo)
    ? paramInfo
    : parseInspirationParamInfo(paramInfo)
  const imageType = String(info.imageType || '').trim().toLowerCase()
  const modelId = String(info.selectedModel || '').trim()
  const rows = []

  if (info.platform) rows.push({ label: '投放平台', value: info.platform })
  if (info.country) rows.push({ label: '目标国家', value: info.country })
  if (info.language) rows.push({ label: '语言', value: info.language })
  if (imageType) rows.push({ label: '生图类型', value: getInspirationImageTypeLabel(imageType) })

  const modelLabel = formatInspirationModelLabel(modelId, detail?.modelName)
  if (modelLabel) rows.push({ label: '生成模型', value: modelLabel })
  if (info.aspectRatio) rows.push({ label: '出图比例', value: info.aspectRatio })

  if (modelId === 'gpt-image-2' && info.quality) {
    rows.push({ label: '出图质量', value: info.quality })
  } else if (info.resolution) {
    rows.push({ label: '分辨率', value: info.resolution })
  }

  return {
    prompt: String(info.prompt || '').trim(),
    imageType,
    aspectRatio: String(info.aspectRatio || '').trim(),
    aspectRatioValue: parseAspectRatioValue(info.aspectRatio),
    rows,
  }
}

/**
 * 详情：合并列表卡片与详情接口字段
 */
export function mergeInspirationDetailCard(card, detail, tabId = '') {
  const mapped = mapInspirationItemToCard({ ...card, ...detail }, tabId || card?.tabId || '')
  if (!mapped) return card
  const paramInfo = parseInspirationParamInfo(detail?.paramInfo)
  const isImage = isInspirationImageCase(paramInfo, mapped.type)
  const generalMaterials = isImage
    ? []
    : isInspirationKeyframeVideo(paramInfo)
      ? parseInspirationKeyframeMaterials(paramInfo)
      : parseInspirationGeneralMaterials(paramInfo.generalMaterials)
  const base = {
    ...card,
    ...mapped,
    paramInfo,
    scenes: parseInspirationScenes(paramInfo),
    generalMaterials,
  }
  if (!isImage) return base
  return {
    ...base,
    resultImages: parseInspirationResultImages(detail, paramInfo),
    productImages: parseInspirationProductImages(paramInfo),
    imageParamSummary: buildInspirationImageParamSummary(paramInfo, detail),
  }
}

/** paramInfo 可能是 JSON 字符串或对象 */
export function parseInspirationParamInfo(raw) {
  if (!raw) return {}
  if (typeof raw === 'string') {
    const text = raw.trim()
    if (!text) return {}
    try {
      const parsed = JSON.parse(text)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (_) {
      return {}
    }
  }
  return typeof raw === 'object' ? raw : {}
}

/** paramInfo.type=video 且 subtype=1：首尾帧视频案例 */
export function isInspirationKeyframeVideo(paramInfo) {
  const info = typeof paramInfo === 'object' && paramInfo !== null && !Array.isArray(paramInfo)
    ? paramInfo
    : parseInspirationParamInfo(paramInfo)
  const type = String(info.type || '').trim().toLowerCase()
  return type === 'video' && Number(info.subtype) === 1
}

function extractInspirationFrameUrl(frame) {
  if (!frame) return ''
  if (typeof frame === 'string') return frame.trim()
  if (typeof frame === 'object') {
    return String(
      frame.url || frame.src || frame.ossUrl || frame.coverUrl || frame.mediaUrl || '',
    ).trim()
  }
  return ''
}

/** 首尾帧案例：从 paramInfo.firstFrame / lastFrame 解析展示素材 */
export function parseInspirationKeyframeMaterials(paramInfo) {
  const info = typeof paramInfo === 'object' && paramInfo !== null && !Array.isArray(paramInfo)
    ? paramInfo
    : parseInspirationParamInfo(paramInfo)
  const materials = []
  const firstUrl = extractInspirationFrameUrl(info.firstFrame)
  const lastUrl = extractInspirationFrameUrl(info.lastFrame)
  if (firstUrl) {
    materials.push({
      id: 'first-frame',
      type: 'image',
      url: firstUrl,
      name: '首帧',
    })
  }
  if (lastUrl) {
    materials.push({
      id: 'last-frame',
      type: 'image',
      url: lastUrl,
      name: '尾帧',
    })
  }
  return materials
}

/** 从 paramInfo.scene 解析适用场景标签 */
export function parseInspirationScenes(paramInfo) {
  const info = typeof paramInfo === 'object' && paramInfo !== null && !Array.isArray(paramInfo)
    ? paramInfo
    : parseInspirationParamInfo(paramInfo)
  const scene = info.scene ?? info.scenes
  if (Array.isArray(scene)) {
    return scene.map((s) => String(s || '').trim()).filter(Boolean)
  }
  if (typeof scene === 'string') {
    return scene.split(/[,，、|]/).map((s) => s.trim()).filter(Boolean)
  }
  return []
}

function inferMaterialType(item) {
  const t = String(item?.type || item?.mediaType || '').trim().toLowerCase()
  if (t === 'video' || t === '视频') return 'video'
  if (t === 'audio' || t === '音频') return 'audio'
  if (t === 'image' || t === '图片') return 'image'
  const url = String(
    item?.url || item?.src || item?.ossUrl || item?.coverUrl || item?.mediaUrl || '',
  ).toLowerCase()
  if (/\.(mp4|webm|mov|m4v|m3u8)(\?|#|$)/i.test(url)) return 'video'
  if (/\.(mp3|wav|ogg|m4a|aac|flac)(\?|#|$)/i.test(url)) return 'audio'
  return 'image'
}

/** 详情 generalMaterials → 页面展示结构 */
export function parseInspirationGeneralMaterials(raw) {
  let list = raw
  if (typeof raw === 'string') {
    const text = raw.trim()
    if (!text) return []
    try {
      list = JSON.parse(text)
    } catch (_) {
      return []
    }
  }
  if (!Array.isArray(list)) return []
  return list
    .map((item, idx) => {
      if (!item || typeof item !== 'object') return null
      const url = String(
        item.url || item.src || item.ossUrl || item.coverUrl || item.mediaUrl || '',
      ).trim()
      if (!url) return null
      const type = inferMaterialType(item)
      return {
        id: String(item.id || `mat-${idx}`),
        type,
        url,
        name: String(item.name || item.title || item.refTag || `素材${idx + 1}`).trim(),
      }
    })
    .filter(Boolean)
}

export function buildCategoryMaps(categories = []) {
  const byName = {}
  const byId = {}
  ;(categories || []).forEach((cat) => {
    if (!cat || cat.id == null) return
    const id = String(cat.id)
    byId[id] = cat
    const name = String(cat.name || '').trim()
    if (name) byName[name.toLowerCase()] = id
  })
  return { byName, byId }
}

export function resolveCategoryIdByName(name, byName) {
  const key = String(name || '').trim().toLowerCase()
  return key ? byName[key] || '' : ''
}

/** 按 sort 升序、id 升序排列二级分类 */
export function sortInspirationCategories(categories = []) {
  return [...categories].sort((a, b) => {
    const sortDiff = (Number(a.sort) || 0) - (Number(b.sort) || 0)
    if (sortDiff !== 0) return sortDiff
    return (Number(a.id) || 0) - (Number(b.id) || 0)
  })
}

/** 二级分类 → 顶部横向 Tab */
export function mapCategoryToTab(cat) {
  const id = String(cat.id)
  return {
    id,
    label: String(cat.name || '').trim(),
    hot: !!Number(cat.isHot),
    categoryId: id,
  }
}

/** 二级分类 → 发现页左侧区块（与 Tab 一一对应） */
export function mapCategoryToDiscoverSection(cat, desc = '') {
  const id = String(cat.id)
  const name = String(cat.name || '').trim()
  return {
    id,
    tabId: id,
    tag: name,
    title: name,
    desc: desc || '通过 AI 技术生成的高质量创意内容，一键复刻同款风格。',
    cards: [],
  }
}
